import { format } from 'date-fns';
import React, { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { GetSocialAnalyticsPostsWithInsightsQuery, SocialPostType } from '@frontend/gql/social/graphql';

import { Card, CardContent, CardHeader, CardTitle } from '@/shadcn/components/ui/card';
import { Skeleton } from '@/shadcn/components/ui/skeleton';

import { POST_TYPE_ORDER, getPostTypeColor, getPostTypeDisplayName } from '../../constants/postTypes';

interface PostsBreakdownByPostTypeHistoricalCardProps {
  data: GetSocialAnalyticsPostsWithInsightsQuery['client']['socialAnalytics']['postsBreakdown'] | undefined;
  fetching: boolean;
}

type ChartDataPoint = {
  name: string;
  date: Date;
} & Partial<Record<SocialPostType, number>>;

const getFormatStrategy = (dateResolution: string | null) => {
  const formatStrategies = {
    DAILY: (date: Date) => format(date, 'M/d/yyyy'),
    WEEKLY: (date: Date) => format(date, 'M/d/yyyy'),
    MONTHLY: (date: Date) => format(date, 'MMMM yyyy'),
    YEARLY: (date: Date) => format(date, 'yyyy'),
  } as const;

  type ResolutionType = keyof typeof formatStrategies;
  return !dateResolution || !(dateResolution in formatStrategies)
    ? formatStrategies.MONTHLY
    : formatStrategies[dateResolution as ResolutionType];
};

const PostsBreakdownByPostTypeHistoricalCard: React.FC<PostsBreakdownByPostTypeHistoricalCardProps> = ({
  data,
  fetching,
}) => {
  const chartData = useMemo(() => {
    if (!data?.byPostType?.historical?.length) return [];

    // Group data by date period - following posts-card pattern
    const groupedData: Record<string, ChartDataPoint> = {};

    data.byPostType.historical.forEach((item) => {
      const startDate = new Date(item.startDate);

      // Use dateResolution to format the period properly
      const formatStrategy = getFormatStrategy(item.dateResolution);
      const groupKey = formatStrategy(startDate);

      if (!groupedData[groupKey]) {
        groupedData[groupKey] = {
          name: groupKey,
          date: startDate,
        };
      }

      const postType = item.postType;
      groupedData[groupKey][postType] = (groupedData[groupKey][postType] || 0) + (item.createdPosts || 0);
    });

    return Object.values(groupedData).sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [data]);

  const postTypeKeys = useMemo(() => {
    if (!chartData.length) return [];

    // Get all post types present in the data
    const presentPostTypes = Object.keys(chartData[0]).filter(
      (key) => key !== 'name' && key !== 'date',
    ) as SocialPostType[];

    // Order them according to POST_TYPE_ORDER
    return POST_TYPE_ORDER.filter((postType) => presentPostTypes.includes(postType));
  }, [chartData]);

  if (fetching) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Posts Created Over Time</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[400px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!chartData.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Posts Created Over Time</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[300px] text-muted-foreground">No data available</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Posts Created Over Time</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tickLine={false} axisLine={false} tickMargin={10} tick={{ fontSize: 12 }} />
              <YAxis tickLine={false} axisLine={false} tickMargin={10} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend
                verticalAlign="bottom"
                align="center"
                layout="horizontal"
                iconSize={10}
                iconType="square"
                wrapperStyle={{
                  position: 'relative',
                  width: '100%',
                }}
                formatter={(value) => <span className="text-muted-foreground ml-1 text-[12px]">{value}</span>}
              />
              {postTypeKeys.map((postType) => {
                const socialPostType = postType as SocialPostType;
                return (
                  <Bar
                    key={postType}
                    dataKey={postType}
                    stackId="postType"
                    fill={getPostTypeColor(socialPostType)}
                    name={getPostTypeDisplayName(socialPostType)}
                  />
                );
              })}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default PostsBreakdownByPostTypeHistoricalCard;
